import { useContext, useEffect, useRef, useState } from "react";
import RelifeBookNow from "./homeLayout/RelifeBookNow";
import RelifeClass from "./homeLayout/RelifeClass";
import RelifeCompany from "./homeLayout/RelifeCompany";
import RelifeContactNew from "./homeLayout/RelifeContactNew";
import RelifeExc from "./homeLayout/RelifeExc";
import RelifeFooter from "./homeLayout/RelifeFooter";
import RelifeOurApproach from "./homeLayout/RelifeOurApproach";
import RelifePlans from "./homeLayout/RelifePlans";
import Animation from "./animation/Animation.jsx";
import "./style/Home.css";
import {
  NavLink,
  Link,
  useNavigate,
  useParams,
  useLocation,
} from "react-router-dom";
import { AuthContext } from "./auth/AuthProvider";

import Navbar from "../components/Navbar.jsx";
import Trust from "./homeLayout/Trust.jsx";
import RelifeContact from "./homeLayout/RelifeContact.jsx";

const LANDING_THEMES = [
  {
    slug: "stress-and-fatigue",
    name: "Stress & Fatigue",
    tagline:
      "For when everything feels heavy, and even small things drain you.",
    available: true,
  },
  {
    slug: "motivation-and-laziness",
    name: "Motivation & Laziness",
    tagline:
      "For when you know what matters — but still can't make yourself move.",
    available: true,
  },
  {
    slug: "overthinking-anxiety",
    name: "Overthinking & Anxiety",
    tagline: "For when your mind won't stop, and clarity feels impossible.",
    available: false,
  },
  {
    slug: "love-and-disappointment",
    name: "Love & Disappointment",
    tagline:
      "For when you're trying to understand what hurt you — and why it still lingers.",
    available: true,
  },
];

const FEELING_CARDS = [
  {
    title: "When you overthink everything",
    text: "Not because you are weak, but because your mind is trying to protect you before you fully understand what it fears.",
  },
  {
    title: "When emotions feel confusing",
    text: "You may know that something hurts, but not yet why it has so much power over you.",
  },
  {
    title: "When patterns keep repeating",
    text: "The same reactions often return until you understand what they are trying to show you.",
  },
];

const HOW_STEPS = [
  {
    num: "01",
    title: "Choose a theme",
    desc: "Start with what feels closest to your current inner struggle — love, stress, confidence, discipline, money, or overthinking.",
  },
  {
    num: "02",
    title: "Answer honestly",
    desc: "Each question is designed to reveal something specific, not to judge you or push you into advice.",
  },
  {
    num: "03",
    title: "Listen to your reflection",
    desc: "After each answer, you receive a calm guided reflection that helps you make sense of what your response may be showing.",
  },
  {
    num: "04",
    title: "Move through five phases",
    desc: "Each theme takes you from recognition, to deeper meaning, to a calmer and more honest inner position.",
  },
];

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, delay, ease: "easeOut" },
});

const heroFade = (delay = 0) => ({
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, delay, ease: "easeOut" },
});
// const apiUrl = process.env.REACT_APP_API_URL;

export default function Home() {
  const [opacity, setOpacity] = useState(0);
  const [opacity1, setOpacity1] = useState(0);

  const {
    payed,
    user,
    googleUser,
    setRedirection,
    redirection,
    themeSlug,
    prevLocation,
  } = useContext(AuthContext);

  const contactRef = useRef(null);

  const { id } = useParams();
  const navigate = useNavigate();

  const location = useLocation();

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("hero-text1");
          observer.unobserve(entry.target);
        }
      });
    });

    document.querySelectorAll(".hero-text").forEach((element) => {
      observer.observe(element);
    });
  }, [payed, navigate, user, googleUser]);

  useEffect(() => {
    // localStorage.setItem("postLoginRedirectTheme", prevLocation);

    if (!user && !googleUser) {
      // localStorage.setItem("postLoginRedirectTheme", prevLocation);
      localStorage.setItem("postLoginRedirect", location.pathname);
      // return;
    }
  }, [user, googleUser, navigate]);

  return (
    <>
      <div className="parentHome">
        <section className="hero">
          <div className="hero__glow" />

          {/* Atmospheric concentric rings — reflection / looking inward */}
          <svg
            className="hero__rings"
            viewBox="0 0 700 700"
            fill="none"
            aria-hidden="true"
          >
            {[300, 250, 200, 155, 115, 80, 50].map((r, i) => (
              <circle
                key={r}
                cx="350"
                cy="350"
                r={r}
                stroke="rgba(199,163,106,0.055)"
                strokeWidth="1"
                style={{ opacity: 1 - i * 0.08 }}
              />
            ))}
            <circle cx="350" cy="350" r="22" fill="rgba(199,163,106,0.06)" />
            <circle cx="350" cy="350" r="8" fill="rgba(199,163,106,0.12)" />
          </svg>

          <Animation effect={"fadeUp"} elementAppear={".hero__inner"}>
            <div className="hero__inner">
              <span className="hero__eyebrow" {...heroFade(0.06)}>
                Guided self-reflection
              </span>

              <h1 className="hero__headline" {...heroFade(0.18)}>
                When your mind feels loud,
                <br />
                Introspecta helps you hear
                <br />
                <em>what's actually true.</em>
              </h1>

              <p className="hero__sub" {...heroFade(0.3)}>
                Answer carefully crafted questions and listen to calm
                reflections that help you understand your emotions, patterns,
                and inner conflicts with more clarity.
              </p>

              <div className="hero__ctas" {...heroFade(0.42)}>
                <a href="/session">
                  <button
                    className="btn-dark-primary"
                    data-testid="button-explore-themes"
                  >
                    Start reflecting →
                  </button>
                </a>
              </div>

              <div className="hero__trust" {...heroFade(0.52)}>
                {[
                  "Private",
                  "Calm audio reflections",
                  "No pressure to perform",
                ].map((label) => (
                  <span key={label} className="hero__trust-item">
                    <span className="hero__trust-dot" />
                    {label}
                  </span>
                ))}
              </div>
            </div>
          </Animation>
        </section>

        {/* <Animation effect={"fadeUp"} elementAppear={".fadeUp"}> */}
        <RelifeExc />
        {/* </Animation> */}

        <RelifeCompany />

        <RelifePlans />

        <RelifeClass
          leyebrow={"Explore themes"}
          lh2={"Choose what you want to understand."}
          lbody={
            "Each theme is a guided inner journey built around one emotional pattern."
          }
        />

        <Trust />

        <RelifeOurApproach />

        <RelifeContactNew />
      </div>
    </>
  );
}
